/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaRecordHome.tsx
 * FILE_ID: JOBHUB-QA-RECORD-HOME-001
 * PURPOSE: Cover thumbnail, millimetre size, and record details on the job QA row.
 * WHY: The highlighted QA already stores this on its record settings.
 */
import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@quotech/tenant-shared/lib/utils'
import type { QuoteRow } from '../../jobDetailTypes'
import { useJobHubDataAdapter } from '../../jobHubDataAdapter'
import { ITP_COVER_IMAGE_DEFAULT_MM } from '../../qa/itpCover'
import { QaPhotoSourceMenu } from '../../qa/QaPhotoSourceMenu'
import type { QaJobFile } from '../../qa/qaTypes'
import { QaCoverSizeFields } from './QaCoverSizeFields'
import { INPUT, QaRecordDetailFields } from './QaRecordDetailFields'
import { coverFileFromQDrive } from './qaCoverFromQDrive'
import { useInspectionRecordSettings } from './useInspectionRecordSettings'

const TRIGGER = 'inline-flex min-h-[44px] items-center rounded-lg border border-border px-3 py-2 text-sm font-semibold text-foreground hover:bg-accent disabled:opacity-50 md:min-h-0'

function cardClass(layout: 'row' | 'sheet') {
  return cn(
    'rounded-xl border border-border bg-card',
    layout === 'row' ? 'min-w-[20rem] max-w-full flex-1 basis-[36rem]' : 'w-full',
  )
}

export function QaRecordHome({
  jobId,
  orgId,
  inspectionId,
  quotes = [],
  sourceQuoteSheetIds = [],
  listQDriveFiles,
  layout,
}: {
  jobId: number
  orgId?: number
  inspectionId: string | null
  quotes?: readonly QuoteRow[]
  sourceQuoteSheetIds?: readonly number[]
  listQDriveFiles: (query?: string) => Promise<QaJobFile[]>
  layout: 'row' | 'sheet'
}) {
  if (inspectionId == null) {
    return (
      <div className={cardClass(layout)}>
        <div className="flex flex-col items-start gap-3 p-3 md:flex-row">
          <div className="shrink-0">
            <div className="h-24 w-40 rounded-md border border-border" />
            <button type="button" className={cn(TRIGGER, 'mt-3')} disabled>
              + Cover Image
            </button>
            <div className="mt-3 grid max-w-[11rem] grid-cols-2 gap-2">
              <label className="block min-w-0 text-sm">
                <span className="mb-1 block">H (mm)</span>
                <input className={INPUT} type="number" disabled readOnly aria-label="H (mm)" value={ITP_COVER_IMAGE_DEFAULT_MM.height} />
              </label>
              <label className="block min-w-0 text-sm">
                <span className="mb-1 block">W (mm)</span>
                <input className={INPUT} type="number" disabled readOnly aria-label="W (mm)" value={ITP_COVER_IMAGE_DEFAULT_MM.width} />
              </label>
            </div>
          </div>
        </div>
      </div>
    )
  }
  return (
    <QaRecordHomeBody
      jobId={jobId}
      orgId={orgId}
      inspectionId={inspectionId}
      quotes={quotes}
      sourceQuoteSheetIds={sourceQuoteSheetIds}
      listQDriveFiles={listQDriveFiles}
      layout={layout}
    />
  )
}

function QaRecordHomeBody({
  jobId,
  orgId,
  inspectionId,
  quotes,
  sourceQuoteSheetIds,
  listQDriveFiles,
  layout,
}: {
  jobId: number
  orgId?: number
  inspectionId: string
  quotes: readonly QuoteRow[]
  sourceQuoteSheetIds: readonly number[]
  listQDriveFiles: (query?: string) => Promise<QaJobFile[]>
  layout: 'row' | 'sheet'
}) {
  const details = useInspectionRecordSettings(inspectionId, jobId, orgId, quotes, sourceQuoteSheetIds)
  const { client } = useJobHubDataAdapter()
  const [detailsOpen, setDetailsOpen] = useState(true)
  const [uploading, setUploading] = useState(false)
  const send = async (file: File) => {
    setUploading(true)
    try {
      // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useInspectionRecordSettings.ts (uploadCover)
      await details.uploadCover(file)
    } finally {
      setUploading(false)
    }
  }
  return (
    <div className={cardClass(layout)}>
      <div className="flex flex-col items-start gap-3 p-3 md:flex-row">
        <div className="shrink-0">
          <div className="flex h-24 w-40 items-center justify-center rounded-md border border-border">
            {details.coverImageUrl ? (
              <img src={details.coverImageUrl} alt="" className="max-h-full max-w-full object-contain" />
            ) : null}
          </div>
          {/* Reused from: packages/job-hub/src/jobs/qa/QaPhotoSourceMenu.tsx (QaPhotoSourceMenu) */}
          <QaPhotoSourceMenu
            className="mt-3"
            mode="photo"
            single
            busy={uploading}
            triggerLabel="+ Cover Image"
            triggerClassName={TRIGGER}
            listQDriveFiles={listQDriveFiles}
            onPickFile={send}
            onPickAsset={async (assetId, filename) => {
              // Reused from: packages/job-hub/src/jobs/tabs/job-qa/qaCoverFromQDrive.ts (coverFileFromQDrive)
              const file = await coverFileFromQDrive(client, assetId, filename || 'cover.jpg')
              await send(file)
            }}
          />
          {uploading ? <p className="mt-2 text-xs text-muted-foreground">Uploading cover photo…</p> : null}
          <div className="mt-3 max-w-[11rem]">
            <QaCoverSizeFields
              inputClassName={INPUT}
              heightMm={details.coverHeightMm}
              widthMm={details.coverWidthMm}
              onHeightMm={details.setCoverHeightMm}
              onWidthMm={details.setCoverWidthMm}
              onCommit={() => { void details.commitCoverSize() }}
            />
          </div>
          {!detailsOpen && details.error ? (
            <p role="alert" className="mt-2 text-xs text-destructive">{details.error}</p>
          ) : null}
        </div>
        <div className="min-w-0 w-full flex-1">
          <button
            type="button"
            className="flex items-center gap-1 text-sm font-semibold text-foreground"
            aria-expanded={detailsOpen}
            onClick={() => setDetailsOpen((open) => !open)}
          >
            Details
            <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', detailsOpen && 'rotate-180')} aria-hidden />
          </button>
          {detailsOpen ? (
            <div className="mt-3 grid max-h-[45vh] gap-3 overflow-auto">
              {/* Reused from: packages/job-hub/src/jobs/tabs/job-qa/QaRecordDetailFields.tsx (QaRecordDetailFields) */}
              <QaRecordDetailFields
                details={details}
                jobId={jobId}
                quotes={quotes}
                sourceQuoteSheetIds={sourceQuoteSheetIds}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
